import type { DataPoint } from "@suse-o11y/data-point";
import { differenceInMilliseconds } from "@suse-o11y/date-time";
import { first } from "@suse-o11y/first";
import { last } from "@suse-o11y/last";
import { max } from "@suse-o11y/max";
import { min } from "@suse-o11y/min";
import { isNonEmptyArray } from "@suse-o11y/non-empty-array";
import type { Point } from "@suse-o11y/point";
import { prop } from "@suse-o11y/prop";
import { round } from "@suse-o11y/round";

const COORDINATES_PRECISION = 3;

interface ChartLayout {
	viewboxWidth: number;
	viewboxHeight: number;
	padding: number;
}

/**
 * Creates a converter that maps {@link DataPoint} values to SVG
 * {@link Point} coordinates within the given layout.
 *
 * Returns `undefined` when the data set is empty or has zero time duration.
 * The returned converter returns `undefined` for null-valued data points.
 */
export function toPoint(
	dataPoints: ReadonlyArray<DataPoint>,
	{ viewboxWidth, viewboxHeight, padding }: ChartLayout,
): undefined | ((dataPoint: DataPoint) => undefined | Point) {
	if (!isNonEmptyArray(dataPoints)) {
		return undefined;
	}

	const [startTime] = first(dataPoints);
	const [endTime] = last(dataPoints);

	const duration = differenceInMilliseconds(endTime, startTime);

	if (duration === 0) {
		return undefined;
	}

	const values = dataPoints.map(prop(1));
	const minValue = min(0, values);
	const maxValue = max(minValue, values);

	const amplitude = maxValue - minValue;
	const drawWidth = viewboxWidth - 2 * padding;
	const drawHeight = viewboxHeight - 2 * padding;

	const scaleX = drawWidth / duration;
	const scaleY = amplitude === 0 ? 0 : drawHeight / amplitude;

	return ([timestamp, value]: DataPoint): undefined | Point => {
		if (value == null) {
			return undefined;
		}

		const x = scaleX * differenceInMilliseconds(timestamp, startTime);
		const y = scaleY * (value - minValue);

		return {
			x: round(padding + x, COORDINATES_PRECISION),
			y: round(padding + drawHeight - y, COORDINATES_PRECISION),
		};
	};
}
